import { dataAPI } from "../api"
import {
    filterQueryMapper,
    orderQueryMapper,
    selectorModelMapper,
    typeQueryMapper,
} from "./utils"
import {
    ODataGetArrayResponse,
    ODataGetResourceResponse,
} from "../models/api/data"
import { TypeModel, Type } from "../models/treeSelector/models"
import { SelectableOptionWithGroup, ValueWithGroup } from "./types"

export const parseGroupValueData = <T extends Type>(
    response:
        | ODataGetArrayResponse<TypeModel[T]>
        | ODataGetResourceResponse<TypeModel[T]>,
    type: T,
): ValueWithGroup[] => {
    if (!("value" in response) && response.Id === undefined) {
        return []
    }
    const responseChildren = "value" in response ? response.value : [response]

    const mapper = selectorModelMapper(type)
    return responseChildren.map((child) => mapper(child))
}

// values with decoration and other values
export const getChildren = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 20,
    ids: string[] = [],
): Promise<SelectableOptionWithGroup> => {
    let filterQuery = filterQueryMapper(filter, type, true)

    if (ids.length > 0) {
        let filterIdsString = ""

        ids.forEach((id, index) => {
            if (index > 0) {
                filterIdsString += " or"
            }
            filterIdsString += ` (id eq ${id})`
        })

        filterQuery = filterQuery
            ? `(${filterQuery})` + ` and (${filterIdsString})`
            : filterIdsString
    }

    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, unknown> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $orderby: orderBy ? orderBy : undefined,
        $count: true,
    }

    // If looking for selected values, we don't paginate.
    if (ids.length === 0) {
        parameters["$top"] = top
        parameters["$skip"] = skip
    }

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

// values with decoration and other values
export const getSelectedChildren = async <T extends Type>(
    type: T,
    filter = "",
    ids: string[] = [],
): Promise<SelectableOptionWithGroup> => {
    let filterQuery = filterQueryMapper(filter, type, true)

    if (ids.length > 0) {
        let filterIdsString = ""
        ids.forEach((id, index) => {
            if (index > 0) {
                filterIdsString += ","
            }
            filterIdsString += id
        })
        filterQuery = filterQuery
            ? `(${filterQuery})` + ` and (id in (${filterIdsString}))`
            : `id in (${filterIdsString})`
    }

    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, unknown> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $orderby: orderBy ? orderBy : undefined,
        $count: true,
    }

    const apiUrl = typeQueryMapper(type)

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(apiUrl, parameters)
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

//plain value without decorations
export const getValues = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 20,
    ids: string[] = [],
): Promise<SelectableOptionWithGroup> => {
    let filterQuery = filterQueryMapper(filter, type, true)

    if (ids.length > 0) {
        let filterIdsString = ""

        ids.forEach((id, index) => {
            if (index > 0) {
                filterIdsString += " or"
            }
            filterIdsString += ` (id eq ${id})`
        })

        filterQuery = filterQuery
            ? `(${filterQuery})` + ` and (${filterIdsString})`
            : filterIdsString
    }

    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, unknown> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $orderby: orderBy ? orderBy : undefined,
        $top: top,
        $skip: skip,
        $count: true,
    }

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

//plain selected value without decorations
export const getSelectedValues = async <T extends Type>(
    type: T,
    filter = "",
    ids: string[] = [],
): Promise<SelectableOptionWithGroup> => {
    let filterQuery = filterQueryMapper(filter, type, true)

    if (ids.length > 0) {
        let filterIdsString = ""
        ids.forEach((id, index) => {
            if (index > 0) {
                filterIdsString += ","
            }
            filterIdsString += id
        })
        filterQuery = filterQuery
            ? `(${filterQuery})` + ` and (id in (${filterIdsString}))`
            : `id in (${filterIdsString})`
    }

    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, unknown> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $orderby: orderBy ? orderBy : undefined,
        $count: true,
    }

    const apiUrl = typeQueryMapper(type)

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(apiUrl, parameters)
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

export const parseSelectorData = <T extends Type>(
    response: TypeModel[T] | TypeModel[T][],
    type: T,
): ValueWithGroup[] => {
    if (!response) {
        return []
    }
    const responseChildren = Array.isArray(response) ? response : [response]
    const mapper = selectorModelMapper(type)
    const childrenToBe = responseChildren.map((child) => {
        const mapped = mapper(child)
        const mappedChild = {
            ...mapped,
        }
        return mappedChild
    })
    return childrenToBe
}

//TODO: right now not in used we removed last group selection checkbox
// we can remove after a proper solution for last group(partially loaded) selection
export const getChildrenForGroup = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
    id: string,
): Promise<SelectableOptionWithGroup> => {
    const parameters: Record<string, unknown> = {
        $filter: `group/id eq ${id}`,
        $count: true,
    }

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}
